import { defineStore } from 'pinia'
import { ref } from 'vue'
import { ElMessage } from 'element-plus'
import { useUserStore } from '@/stores/user'
import { useWalletStore } from '@/stores/wallet'
import { membershipApi, type UserMembership } from '@/api/membership'

export const useMembershipStore = defineStore('membership', () => {
  /** 当前用户会员实例，取查询结果 list[0] */
  const current = ref<UserMembership | null>(null)

  let inflight: Promise<boolean> | null = null

  const hasValidRefreshToken = () => {
    const userStore = useUserStore()
    const refreshToken = userStore.userInfo?.refreshToken || localStorage.getItem('refreshToken')
    return userStore.isLoggedIn && !!refreshToken
  }

  const reset = () => {
    inflight = null
    current.value = null
  }

  const resolveIsVip = (membership: UserMembership | null): number => {
    if (!membership) {
      return 0
    }

    const planCode = membership.membershipPlanCode
    const status = membership.membershipStatus
    const planOk = typeof planCode === 'string' && planCode.toUpperCase() !== 'FREE'
    const statusOk = typeof status === 'number' && status === 1
    return planOk && statusOk ? 1 : 0
  }

  /** 基于最新 userInfo 写入 isVip，避免与 applyTokens 刷新令牌互相覆盖 */
  const persistIsVip = (membership: UserMembership | null) => {
    const userStore = useUserStore()
    userStore.applyIsVip(resolveIsVip(membership))
  }

  /** 查询会员实例并写入 list[0] */
  const queryAndStoreCurrent = async (): Promise<'has' | 'empty' | 'fail'> => {
    const res = await membershipApi.queryMine()
    if (!hasValidRefreshToken()) {
      return 'fail'
    }
    if (res?.code !== 0) {
      console.error('[membershipStore] 查询会员实例失败:', res?.message)
      return 'fail'
    }

    const list = res.data?.list
    if (Array.isArray(list) && list.length > 0) {
      current.value = list[0]
      persistIsVip(list[0])
      return 'has'
    }

    current.value = null
    persistIsVip(null)
    return 'empty'
  }

  /**
   * 已登录且刷新令牌有效时查询当前用户会员实例。
   * 无实例，或实例状态不是生效/冻结时，继续查询免费领取资格；可领取则领取后刷新实例和钱包。
   */
  const fetchMemberships = async (): Promise<boolean> => {
    if (!hasValidRefreshToken()) {
      return false
    }
    if (inflight) {
      return inflight
    }

    inflight = (async () => {
      try {
        const first = await queryAndStoreCurrent()
        if (first === 'fail' || !hasValidRefreshToken()) {
          return false
        }

        // 已有生效(1)或冻结(2)会员时，不再走免费领取流程
        if (first === 'has') {
          const status = current.value?.membershipStatus
          if (status === 1 || status === 2) {
            return true
          }
        }

        const eligibilityRes = await membershipApi.queryFreeClaimEligibility()
        if (!hasValidRefreshToken()) {
          return false
        }
        if (eligibilityRes?.code !== 0) {
          console.error('[membershipStore] 查询免费领取资格失败:', eligibilityRes?.message)
          return false
        }
        if (eligibilityRes.data?.canClaim !== true) {
          return true
        }

        const claimRes = await membershipApi.claimFree()
        if (!hasValidRefreshToken()) {
          return false
        }
        if (claimRes?.code !== 0) {
          console.error('[membershipStore] 领取免费会员失败:', claimRes?.message)
          return false
        }

        ElMessage.success('每月免费套餐潮币已到账')
        await queryAndStoreCurrent()
        const walletStore = useWalletStore()
        await walletStore.fetchAccounts(true)
        return true
      } catch (error) {
        console.error('[membershipStore] 查询会员实例失败:', error)
        return false
      } finally {
        inflight = null
      }
    })()

    return inflight
  }

  return {
    current,
    fetchMemberships,
    reset,
  }
})
